import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { observer, Observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { ExclamationCircleOutlined, HourglassOutlined } from '@ant-design/icons';
import { Input, Tooltip, Table, Switch, FloatButton, Modal, Button } from 'antd';
import { Waypoint } from 'react-waypoint';

import { AI_COMMENT_STATUS } from 'src/constants/normal';
import { EDITABLE_TOPIC_KEYS, EDITABLE_TOPIC_TABLE } from 'src/constants/table';
import i18n from 'src/i18n';
import { isNegativeText } from 'src/utils';
import withRouter from 'src/components/withRouter';
import NormalButton from 'src/components/CustomizeButton/NormalButton';
import CustomizeSelect from 'src/components/CustomizeSelect';

import BackIcon from 'src/assets/common/back.svg';
import RefreshIcon from 'src/assets/common/refresh.svg';

import styles from './styles.module.scss';
import './customize.scss';

@observer
class DirectClientMarketingResultPage extends React.Component {
  componentDidMount() {
    this.props.viewModel.didMount(this.props.router.params);
  }

  componentWillUnmount() {
    this.props.viewModel.willUnmount();
  }

  render() {
    const { viewModel } = this.props;
    return (
      <>
        <div className={styles.pageContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.pageTitle}>
              <div>
                {`「${viewModel.newsletterName}」${i18n.t('result_page_title')}`}
              </div>
              <div className={styles.timeText}>
                {viewModel.timeText}
              </div>
            </div>
            <div className={styles.links}>
              <Link
                to={`/newsletter/${viewModel.newsletterId}`}
                className={styles.backBtn}
              >
                <img src={BackIcon} alt="" />
                {i18n.t('common_back_page')}
              </Link>
              <Link
                to={`/newsletter/${viewModel.newsletterId}/result/${viewModel.resultId}/preview`}
                className={styles.previewBtn}
                target="_blank"
              >
                {i18n.t('result_edit_page_preview')}
              </Link>
            </div>
          </div>
          {/* information: summary */}
          <div className={styles.summaryContainer}>
            <div className={styles.summaryTitle}>
              {i18n.t('result_edit_page_summary_title')}
            </div>
            <div className={styles.mainBrandSummaryContainer}>
              <div className={styles.summaryContent}>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryTitle}>
                    {`${viewModel.mainBrand?.name ?? i18n.t('common_default_main_brand')} ${i18n.t('result_edit_page_today_volume')}`}
                  </div>
                  <div className={styles.summaryContent}>
                    {`${viewModel.mainBrandWOMCount} ${i18n.t('common_count_unit')}`}
                  </div>
                </div>
                {
                  (viewModel.manualStatistics.length === 0)
                  && (
                    <div className={styles.summaryRow}>
                      <div className={styles.summaryTitle}>
                        {`${viewModel.mainBrand?.name ?? i18n.t('common_default_main_brand')} ${i18n.t('result_edit_page_sentiment')}`}
                      </div>
                      <div className={styles.summaryContent}>
                        {
                          viewModel.mainBrandSentiments.map((sentiment, i) => {
                            return (
                              <span
                                key={sentiment.key}
                              >
                                {`${sentiment.name} ${sentiment.count} ${i18n.t('common_count_unit')} ${i !== 2 ? '/ ' : ''}`}
                              </span>
                            );
                          })
                        }
                      </div>
                    </div>
                  )
                }

                <div className={styles.summaryRow}>
                  <div className={styles.summaryTitle}>
                    {`${viewModel.mainBrand?.name ?? i18n.t('common_default_main_brand')} ${i18n.t('result_edit_page_top3_channel')}`}
                  </div>
                  <div className={styles.summaryContent}>
                    {
                      viewModel.mainBrandChannels.map((channel, i) => {
                        return (
                          <span
                            key={channel.id}
                          >
                            {`${channel.name} ${i + 1 !== viewModel.mainBrandChannels?.length ? '/ ' : ''}`}
                          </span>
                        );
                      })
                    }
                    {
                      viewModel.mainBrandChannels.length === 0
                      && (
                        <span>
                          {i18n.t('common_no_data')}
                        </span>
                      )
                    }
                  </div>
                </div>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryTitle}>
                    {`${viewModel.mainBrand?.name ?? i18n.t('common_default_main_brand')} ${i18n.t('result_edit_page_top3_feature')}`}
                  </div>
                  <div className={styles.summaryContent}>
                    {
                      viewModel.mainBrandFeatures.map((feature, i) => {
                        return (
                          <span
                            key={feature.id}
                          >
                            {`${feature.name} ${i + 1 !== viewModel.mainBrandFeatures?.length ? '/ ' : ''}`}
                          </span>
                        );
                      })
                    }
                    {
                      viewModel.mainBrandFeatures.length === 0
                      && (
                        <span>
                          {i18n.t('common_no_data')}
                        </span>
                      )
                    }
                  </div>
                </div>
              </div>
              <div className={styles.actionBtnsContainer}>
                <NormalButton
                  onClick={viewModel.onRefreshSummary}
                  icon={<img src={RefreshIcon} alt="" className={styles.refreshIcon} />}
                  className={styles.refreshBtn}
                >
                  {i18n.t('result_edit_page_calculate_btn')}
                </NormalButton>
                {
                  viewModel.autoSummary
                  && (
                    <NormalButton
                      onClick={viewModel.onRefreshAutoSummary}
                      icon={<img src={RefreshIcon} alt="" className={styles.refreshIcon} />}
                      className={styles.refreshBtn}
                    >
                      {i18n.t('result_edit_page_auto_summary_btn')}
                    </NormalButton>
                  )
                }
              </div>
            </div>
            <div className={styles.manualSummary}>
              <div className={styles.manualTitle}>
                <div className={styles.titleText}>
                  {`${i18n.t('result_edit_page_manual_statistic')} - ${i18n.t('result_edit_page_topic_table_eval_type')}`}
                </div>
                <Tooltip
                  title={i18n.t('result_edit_page_manual_tooltip')}
                  overlayStyle={{ maxWidth: 'unset', whiteSpace: 'nowrap' }}
                  placement="bottomLeft"
                >
                  <ExclamationCircleOutlined />
                </Tooltip>
              </div>

              {
                viewModel.manualStatistics.map((item) => {
                  return (
                    <div
                      className={styles.manualRow}
                      key={item.id}
                    >
                      <div className={styles.rowTitle}>
                        {`${item.name}：`}
                      </div>
                      <div className={styles.rowContent}>
                        {
                          item.rating.map((rating, i) => {
                            return (
                              <span key={rating.id}>
                                {`${rating.name} ${rating.count} ${i18n.t('common_count_unit_post')} ${i !== item.rating.length - 1 ? '/ ' : ''}`}
                              </span>
                            );
                          })
                        }
                      </div>
                    </div>
                  );
                })
              }
            </div>
            <div className={styles.memoContainer}>
              <div className={styles.memoTitle}>
                {i18n.t('result_edit_page_memo')}
              </div>
              <Input.TextArea
                className={styles.input}
                placeholder={i18n.t('result_edit_page_memo_placeholder')}
                autoSize={{ minRows: 2 }}
                showCount
                maxLength={5000}
                value={viewModel.memo}
                onChange={viewModel.onMemoChange}
                onBlur={viewModel.onMemoEditEnd}
                disabled={viewModel.isMemoUpdating}
              />
            </div>
            {
              viewModel.isSummaryCalculate
              && (
                <div className={styles.curtain} />
              )
            }
          </div>
          {/* information: topics */}
          <div className={styles.topicsContainer}>
            <div className={styles.actionRow}>
              <div className={styles.title}>
                {i18n.t('result_edit_page_edit_topic')}
              </div>
              <div className={styles.actions}>
                {
                  viewModel.aiComment && viewModel.selectedSubjectContent?.hasEvaluation
                  && (
                    <>
                      <div className={styles.aiCommentStatus}>
                        {viewModel.aiCommentStatusDesc}
                      </div>
                      {
                        viewModel.aiCommentStatus === AI_COMMENT_STATUS.Done
                        || viewModel.aiCommentStatus === AI_COMMENT_STATUS.Fail
                        || !viewModel.aiCommentStatus
                          ? (
                            <NormalButton
                              onClick={viewModel.onRegenerateAiComment}
                              icon={<img src={RefreshIcon} alt="" className={styles.refreshIcon} />}
                              className={styles.refreshBtn}
                            >
                              {i18n.t('result_edit_page_ai_comment_regenerate')}
                            </NormalButton>
                          )
                          : (
                            <NormalButton
                              onClick={viewModel.getAiCommentStatus}
                              icon={<HourglassOutlined />}
                              className={clsx(styles.refreshBtn, viewModel.remainingTime < 30 && styles.disabled)}
                              disabled={viewModel.remainingTime < 30}
                            >
                              {viewModel.remainingTime < 30
                                ? `${i18n.t('result_edit_page_ai_comment_status_refresh')} (${Math.ceil(30 - viewModel.remainingTime)}s)`
                                : i18n.t('result_edit_page_ai_comment_status_refresh')}
                            </NormalButton>
                          )
                      }
                    </>
                  )
                }

                <CustomizeSelect
                  options={viewModel.subjectList}
                  value={viewModel.selectedSubject}
                  onChange={viewModel.onSubjectSelect}
                  className={styles.subjectSelect}
                  placeholder={i18n.t('result_edit_page_select_topic_placeholder')}
                />
              </div>

            </div>
            <div className={styles.tableContainer}>
              <Table
                dataSource={viewModel.topics}
                className={clsx('topicListTable')}
                rowKey={(data) => data.id}
                scroll={{ x: 1784 }} // x: 1920 - 80(outer padding) - 56(inner padding)
                pagination={false}
                style={{ width: viewModel.width - 128 }}
              >
                {
                  EDITABLE_TOPIC_TABLE.map((column) => {
                    return (
                      <Table.Column
                        title={() => {
                          if (column.key === 'brand' && viewModel.searchAngle !== 'brand') {
                            return i18n.t('common_brand_category');
                          }
                          return column.title;
                        }}
                        dataIndex={column.dataIndex}
                        key={column.key}
                        width={column.width}
                        fixed={column.fixed}
                        render={(text, record, index) => {
                          if (column.key === EDITABLE_TOPIC_KEYS.Order) {
                            return (
                              <div className={styles.row}>
                                {index + 1}
                              </div>
                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.Title) {
                            return (
                              <>
                                <a
                                  href={record.url}
                                  className={styles.oneLineRow}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {text}
                                </a>
                                {
                                  (index === viewModel.topics.length - 1 && (viewModel.hasNextPage || viewModel.remainingTopicList.length > 0))
                                  && (
                                    <Waypoint
                                      onEnter={viewModel.processTopics}
                                    />
                                  )

                                }
                              </>

                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.Sentiment) {
                            return (
                              <div
                                className={
                                  clsx(
                                    styles.dot,
                                    record.sentiment === 1 && styles.positive,
                                    record.sentiment === 0 && styles.neutral,
                                    record.sentiment === -1 && styles.negative
                                  )
                                }
                              />
                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.EvalType) {
                            if (viewModel.selectedSubjectContent.hasEvaluation) {
                              return (
                                <Observer>
                                  {() => {
                                    return (
                                      <CustomizeSelect
                                        value={record.evalType}
                                        options={viewModel.ratingTags}
                                        onChange={(value) => viewModel.onEvalTypeChange(value, record.id, record.brandId)}
                                        className={clsx(styles.select, isNegativeText(viewModel.ratingTagsObject[record.evalType]) && styles.negative)}
                                        placeholder={i18n.t('result_edit_page_topic_table_eval_type_placeholder')}
                                        popupClassName="resultPopup"
                                      />
                                    );
                                  }}
                                </Observer>

                              );
                            }
                            return (
                              <div className={styles.row}>
                                ---
                              </div>
                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.TopicAttr) {
                            if (viewModel.selectedSubjectContent.hasEvaluation) {
                              return (
                                <Observer>
                                  {() => {
                                    return (
                                      <CustomizeSelect
                                        value={record.topicAttr}
                                        options={viewModel.topicTags}
                                        onChange={(value) => viewModel.onTopicAttrChange(value, record.id, record.brandId)}
                                        className={styles.select}
                                        placeholder={i18n.t('result_edit_page_topic_table_topic_attribute_placeholder')}
                                      />
                                    );
                                  }}
                                </Observer>

                              );
                            }
                            return (
                              <div className={styles.row}>
                                ---
                              </div>
                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.ReportAdvice) {
                            if (viewModel.selectedSubjectContent.hasEvaluation) {
                              return (
                                <Observer>
                                  {() => {
                                    return (
                                      <CustomizeSelect
                                        value={record.reportAdvice}
                                        options={viewModel.suggestionTags}
                                        onChange={(value) => viewModel.onReportAdviceChange(value, record.id, record.brandId)}
                                        className={styles.select}
                                        placeholder={i18n.t('result_edit_page_topic_table_report_advice_placeholder')}
                                      />
                                    );
                                  }}
                                </Observer>

                              );
                            }
                            return (
                              <div className={styles.row}>
                                ---
                              </div>
                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.DepartmentAttr) {
                            if (viewModel.selectedSubjectContent.hasEvaluation) {
                              return (
                                <Observer>
                                  {() => {
                                    return (
                                      <CustomizeSelect
                                        value={record.departmentAttr}
                                        options={viewModel.departmentTags}
                                        onChange={(value) => viewModel.onDepartmentAttrChange(value, record.id, record.brandId)}
                                        className={styles.select}
                                        placeholder={i18n.t('result_edit_page_topic_table_department_placeholder')}
                                      />
                                    );
                                  }}
                                </Observer>

                              );
                            }
                            return (
                              <div className={styles.row}>
                                ---
                              </div>
                            );
                          }
                          if (column.key === EDITABLE_TOPIC_KEYS.ShouldSend) {
                            return (
                              <Observer>
                                {() => {
                                  return (
                                    <Switch
                                      checked={!record.shouldSend}
                                      className={clsx(styles.switch, !record.shouldSend && styles.activeSwitch)}
                                      onChange={(boolean) => viewModel.onShouldSendChange(!boolean, record.id, record.brandId)}
                                    />
                                  );
                                }}
                              </Observer>

                            );
                          }
                          return (
                            <div className={styles.row}>
                              {text}
                            </div>
                          );
                        }}
                      />
                    );
                  })
                }
              </Table>
            </div>
          </div>

        </div>
        <FloatButton.BackTop
          visibilityHeight={100}
          target={() => document.querySelector('.main')}
          tooltip="回頂部"
        />
        <Modal
          open={viewModel.isShowRefreshAutoSummaryModal}
          footer={null}
          centered
          closeIcon={null}
          onCancel={() => viewModel.refreshAutoSummaryResolve(false)}
          className={styles.refreshAutoSummaryModal}
          width="unset"
          styles={{
            content: {
              padding: 0
            }
          }}
        >
          <div className={styles.refreshContainer}>
            <div className={styles.refreshTitle}>
              {i18n.t('result_edit_page_auto_summary_title')}
            </div>
            <div className={styles.refreshDescription}>
              {i18n.t('result_edit_page_auto_summary_desc')}
            </div>
            <div className={styles.refreshBtnContainer}>
              <Button
                onClick={() => viewModel.refreshAutoSummaryResolve(true)}
                className={styles.confirmBtn}
              >
                {i18n.t('result_edit_page_auto_summary_confirm')}
              </Button>
              <Button
                onClick={() => viewModel.refreshAutoSummaryResolve(false)}
                className={styles.cancelBtn}
              >
                {i18n.t('result_edit_page_auto_summary_cancel')}
              </Button>
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

DirectClientMarketingResultPage.propTypes = {
  router: PropTypes.object,
  viewModel: PropTypes.object
};

DirectClientMarketingResultPage.defaultProps = {
  router: {},
  viewModel: {}
};

export default withRouter(DirectClientMarketingResultPage);
